import { useQuery } from '@tanstack/react-query';
import type { AriPushBatchRange } from '@/shared/api';
import { useApi } from '@/shared/api';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { formatDateRange } from '@/shared/lib/format';

interface AriOwnershipImpactDialogProps {
  open: boolean;
  propertyId: string;
  ranges: AriPushBatchRange[];
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

export function AriOwnershipImpactDialog({
  open,
  propertyId,
  ranges,
  saving,
  onOpenChange,
  onConfirm,
}: AriOwnershipImpactDialogProps) {
  const api = useApi();
  const ownershipRanges = ranges.filter((range) => range.rate != null || range.minStay != null);
  const impact = useQuery({
    queryKey: [
      'channex',
      'ariOwnershipImpact',
      propertyId,
      ownershipRanges.map((range) => [range.from, range.to, range.rate, range.minStay]),
    ],
    queryFn: () =>
      Promise.all(
        ownershipRanges.map((range) =>
          api.channex.ariOwnershipImpact(propertyId, range.from, range.to),
        ),
      ),
    enabled: open && ownershipRanges.length > 0,
    retry: false,
  });
  const ready = ownershipRanges.length === 0 || impact.isSuccess;

  return (
    <Dialog open={open} onOpenChange={(value) => !saving && onOpenChange(value)}>
      <DialogContent showClose={!saving}>
        <DialogHeader>
          <DialogTitle>Änderungen prüfen</DialogTitle>
          <DialogDescription>
            StayOn merkt die Änderung lokal vor und überträgt sie anschließend asynchron an den
            Kanalmanager. Der Verlauf je Apartment zeigt, was gesendet wurde.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {ownershipRanges.length === 0 && (
            <p className="text-ink-soft text-sm">
              Diese Änderung übernimmt keinen Preis und keinen Mindestaufenthalt aus der Automatik.
              Die bestehende Zuständigkeit bleibt unverändert.
            </p>
          )}
          {impact.isLoading && (
            <p role="status" className="text-ink-soft text-sm">
              Auswirkung wird geladen…
            </p>
          )}
          {impact.isError && (
            <p role="alert" className="text-sealwax text-sm">
              Die Auswirkungen auf die Preisautomatik konnten nicht geladen werden. Es wird nichts
              vorgemerkt.
            </p>
          )}
          {impact.data && (
            <ul className="space-y-3">
              {impact.data.map((item, index) => {
                const range = ownershipRanges[index]!;
                return (
                  <li
                    key={`${range.from}-${range.to}-${index}`}
                    className="border-line/50 rounded-md border p-3"
                  >
                    <p className="text-ink text-sm font-semibold">
                      {formatDateRange(range.from, range.to)}
                    </p>
                    {range.rate != null && item.automationRateDays > 0 && (
                      <p className="text-ink-soft mt-1 text-xs">
                        Preis: {days(item.automationRateDays)} gehen von der Preisautomatik in die
                        manuelle Pflege über.
                      </p>
                    )}
                    {range.minStay != null && item.automationMinimumStayDays > 0 && (
                      <p className="text-ink-soft mt-1 text-xs">
                        Mindestaufenthalt: {days(item.automationMinimumStayDays)} gehen von Dynamic
                        Pricing in die manuelle Pflege über.
                      </p>
                    )}
                    {item.alreadyManualDays > 0 && (
                      <p className="text-ink-faint mt-1 text-xs">
                        {days(item.alreadyManualDays)} werden bereits manuell gepflegt.
                      </p>
                    )}
                    {noHandover(item, range) && (
                      <p className="text-ink-faint mt-1 text-xs">
                        Keine Zuständigkeit wechselt aus der Preisautomatik.
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Zurück
          </Button>
          <Button variant="brand" onClick={onConfirm} disabled={!ready} loading={saving}>
            Änderungen vormerken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function days(value: number) {
  return `${value} ${value === 1 ? 'Tag' : 'Tage'}`;
}

function noHandover(
  item: {
    automationRateDays: number;
    automationMinimumStayDays: number;
    alreadyManualDays: number;
  },
  range: AriPushBatchRange,
) {
  const rateDays = range.rate != null ? item.automationRateDays : 0;
  const stayDays = range.minStay != null ? item.automationMinimumStayDays : 0;
  return rateDays === 0 && stayDays === 0 && item.alreadyManualDays === 0;
}
